import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Logout,
  Person,
  Login as LoginIcon,
  PersonAdd,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { safeConsoleError } from '../utils/safeConsole';

const AuthHeader: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      safeConsoleError('Logout error', 'auth.header.logout');
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/register');
  };

  const getUserInitials = (user: any): string => {
    if (!user) return '';
    const firstName = user.given_name || user.name?.split(' ')[0] || '';
    const lastName = user.family_name || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getUserDisplayName = (user: any): string => {
    if (!user) return '';
    return user.given_name || user.name || user.email || 'Usuario';
  };

  const getUserFullName = (user: any): string => {
    if (!user) return '';
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user.name || user.email || 'Usuario';
  };

  return (
    <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          Open e-CMR
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAuthenticated ? (
            <>
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                Hola, {getUserDisplayName(user)}
              </Typography>
              
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {getUserInitials(user)}
                </Avatar>
              </IconButton>
              
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem disabled>
                  <Person sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {getUserFullName(user)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email}
                    </Typography>
                  </Box>
                </MenuItem>
                
                <Divider />
                
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Cerrar sesión
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' }, opacity: 0.8 }}>
                Nube requerida
              </Typography>
              
              <Button
                color="inherit"
                startIcon={<LoginIcon />}
                onClick={handleLogin}
                size="small"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                Iniciar sesión
              </Button>
              
              <Button
                color="inherit"
                startIcon={<PersonAdd />}
                onClick={handleRegister}
                variant="outlined"
                size="small"
                sx={{ 
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Registrarse
              </Button>

              {/* Mobile buttons */}
              <IconButton color="inherit" onClick={handleLogin} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <LoginIcon />
              </IconButton>
              <IconButton color="inherit" onClick={handleRegister} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <PersonAdd />
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AuthHeader;
